import { StateCreator } from 'zustand';
import { PersistOptions, persist } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

import { RestResponse } from '../../api/Rest';
import {
  getCurrentUser as restGetCurrentUser,
  CollectedNameCardData,
  acceptNameCardTransfer as restAcceptNameCardTransfer,
  collectNameCard as restCollectNameCard,
  getInvitationLink as restGetInvitationLink,
  getNameCard as restGetNameCard,
  getRedemptionCode as restGetRedemptionCode,
  getUserAllowance as restGetUserAllowance,
} from '../../api/Rest/user';
import {
  acceptInvitationEmail as restAcceptInvitationEmail,
  createSystemInvitation as restCreateSystemInvitation,
  deleteSystemInvitation as restDeleteSystemInvitation,
  getInvitations as restGetInvitations,
  getInvitation as restGetInvitation,
  getInvitationUser as restGetInvitationUser,
  getSystemInvitation as restGetSystemInvitation,
  getSystemInvitations as restGetSystemInvitations,
  resetPassword as restResetPassword,
  forgotPassword as restForgotPassword,
  sendVerification as restSendVerification,
  userCodeValidation as restUserCodeValidation,
  findProfile as restfindProfile,
  searchProfiles as restSearchProfiles,
  sentInvitationEmail as restSentInvitationEmail,
  sentPasswordEmail as restSentPasswordEmail,
  updateSystemInvitation as restUpdateSystemInvitation,
  verify,
} from '../../api/Rest/users';
import {
  AlbumListType,
  GameListType,
  MultiplayerGameListType,
  SpaceLeaderboardListType,
  SpaceListType,
  SpaceTeamListType,
} from '../../models/SpaceStore';
import {
  StorageValue,
  StoreLifecycle,
  StoreLifecycleAction,
  createStoreListFieldHook,
  createStoreMapFieldHook,
} from '../../models/Store';
import { Account } from '../../models/UserStore/Account';
import {
  Invitation,
  InvitationLink,
  InvitationType,
  RedemptionCode,
  SystemInvitation,
  SystemInvitationType,
} from '../../models/UserStore/Invitation';
import { NameCard } from '../../models/UserStore/NameCard';
import { Profile } from '../../models/UserStore/Profile';
import {
  AccountRepository,
  InvitationLinkRepository,
  InvitationRepository,
  NameCardListType,
  NameCardRepository,
  ProfileListType,
  ProfileMapType,
  ProfileRepository,
  RedemptionCodeListType,
  RedemptionCodeRepository,
  ResetPasswordResult,
  SystemInvitationRepository,
  UserListType,
  UserStoreAction,
  UserStoreState,
  UserStoreStateJson,
} from '../../models/UserStore/UserStore';
import { Allowance } from '../../models/UserStore';
import { NonEmptyArray } from '../../models/Utils';
import { array } from '../../utils/array';
import { dateNumberReviver } from '../../utils/json';
import { useFileStore } from '../FileStore';
import { useSpaceStore } from '../SpaceStore';
import * as State from './state';
import { useSessionStore } from '../SessionStore';
import { RegionsCode } from '../../constants/region';

type Store = StoreLifecycleAction & UserStoreState & UserStoreAction;

const lifecycle = new StoreLifecycle('UserStore');

const initializer: StateCreator<Store, [], []> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    () => {
      // do nothing
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    () => {
      // clear data
      set((state) => {
        state.accounts.clear(
          UserListType.Current,
          UserListType.Main,
          UserListType.Sub,
        );
        state.profiles.clear(
          UserListType.Current,
          UserListType.Main,
          UserListType.Sub,
        );
        return { ...state };
      });
    },
    () => set((state) => ({ ...state })),
  );

  const setupActiveUserInfo: UserStoreAction['setupActiveUserInfo'] = (
    current,
    main,
    subs,
  ) => {
    set((state) => {
      if (main?.account) {
        state.accounts.clear(UserListType.Main);
        state.addAccounts(UserListType.Main, new Account(main.account));
      }
      if (main?.profile) {
        state.profiles.clear(UserListType.Main);
        state.addProfiles(UserListType.Main, new Profile(main.profile));
        useSessionStore
          .getState()
          .setActiveRegion(
            (main.profile.region as RegionsCode) ?? RegionsCode.US,
          );
      }
      if (subs) {
        const { profiles, accounts } = subs.reduce<{
          profiles: Profile[];
          accounts: Account[];
        }>(
          (acc, { profile, account }) => {
            if (account) {
              acc.accounts.push(new Account(account));
            }
            if (profile) {
              acc.profiles.push(new Profile(profile));
            }
            return acc;
          },
          { profiles: [], accounts: [] },
        );
        if (accounts.length > 0) {
          state.accounts.clear(UserListType.Sub);
          state.addAccounts(UserListType.Sub, ...accounts);
        }
        if (profiles.length > 0) {
          state.profiles.clear(UserListType.Sub);
          state.addProfiles(UserListType.Sub, ...profiles);
        }
      }
      if (current) {
        state.accounts.clear(UserListType.Current);
        state.profiles.clear(UserListType.Current);
        state.addAccounts(
          [UserListType.Current, UserListType.RecentLogin],
          new Account(current.account),
        );
        state.addProfiles(
          [UserListType.Current, UserListType.RecentLogin],
          new Profile(current.profile),
        );
        useSessionStore
          .getState()
          .setActiveRegion(
            (current.profile.region as RegionsCode) ?? RegionsCode.US,
          );
        const mainUserId =
          current.account.mainAccountUserId ?? current.account.userId;
        const mainAccounts = state.accounts.list(UserListType.Main).get();
        if (!mainAccounts.some(({ userId }) => userId === mainUserId)) {
          state.accounts.clear(UserListType.Main);
          state.profiles.clear(UserListType.Main);
        }
        const subaccounts = state.accounts.list(UserListType.Sub).get();
        if (
          !subaccounts.some(
            ({ mainAccountUserId }) => mainAccountUserId === mainUserId,
          )
        ) {
          state.accounts.clear(UserListType.Sub);
          state.profiles.clear(UserListType.Sub);
        }
      }
      return { ...state };
    });
  };

  const setProfiles: UserStoreAction['setProfiles'] = (profiles, fieldName) => {
    set((state) => {
      state.profiles
        .lists(array(fieldName) as NonEmptyArray<string>)
        .set(...array(profiles));
      return { ...state };
    });
  };

  const searchProfiles: UserStoreAction['searchProfiles'] = async (
    params = {},
    options,
  ) => {
    const { ok, data } = await restSearchProfiles(
      { limit: 20, cursor: undefined, ...params },
      options,
    );

    if (ok) {
      set((state) => {
        const field = state.profiles.list(ProfileListType.SearchResult);
        const profiles = data.profiles.map(
          (profile) => new Profile(profile.profile),
        );
        if (params.cursor === undefined) {
          field.set(...profiles);
        } else {
          field.push(...profiles);
        }

        field.setCursor(data.cursor ?? null);
        return { ...state };
      });
    }
    return {
      shouldFetchMore: !!data?.cursor,
    };
  };

  const getProfiles: UserStoreAction['getProfiles'] = (fieldName) =>
    get().profiles.list(fieldName).get();

  const addProfiles: UserStoreAction['addProfiles'] = (
    fieldNames,
    ...profiles
  ) => {
    set((state) => {
      const fns = array(fieldNames);
      if (fns.includes(UserListType.RecentLogin)) {
        const field = state.profiles.list(UserListType.RecentLogin);

        field.unshift(...profiles);
        field.prune();
      }

      const otherFieldNames = fns.filter(
        (fieldName) =>
          ![UserListType.RecentLogin].includes(fieldName as UserListType),
      );
      if (otherFieldNames.length > 0) {
        state.profiles
          .lists(otherFieldNames as NonEmptyArray<string>)
          .push(...profiles);
      }

      return { ...state };
    });
  };

  const setAccounts: UserStoreAction['setAccounts'] = (accounts, fieldName) => {
    set((state) => {
      state.accounts
        .lists(array(fieldName) as NonEmptyArray<string>)
        .set(...array(accounts));
      return { ...state };
    });
  };

  const addAccounts: UserStoreAction['addAccounts'] = (
    fieldNames,
    ...accounts
  ) => {
    set((state) => {
      const fns = array(fieldNames);
      if (fns.includes(UserListType.RecentLogin)) {
        const field = state.accounts.list(UserListType.RecentLogin);

        field.unshift(...accounts);
        field.prune();
      }

      const otherFieldNames = fns.filter(
        (fieldName) =>
          ![UserListType.RecentLogin].includes(fieldName as UserListType),
      );
      if (otherFieldNames.length > 0) {
        state.accounts
          .lists(otherFieldNames as NonEmptyArray<string>)
          .push(...accounts);
      }

      return { ...state };
    });
  };
  const getProfile: UserStoreAction['getProfile'] = (userId) =>
    get().profiles.get(userId);
  const getAccount: UserStoreAction['getAccount'] = (userId) =>
    get().accounts.get(userId);
  const getProfileFromMap: UserStoreAction['getProfileFromMap'] = (
    fieldName,
    key,
  ) => get().profiles.map(fieldName).get(key);

  const deleteProfileFromMap: UserStoreAction['deleteProfileFromMap'] = (
    fieldName,
    id,
  ) => {
    set((state) => {
      state.profiles.map(fieldName).delete(id);
      return { ...state };
    });
  };

  const addProfilesWithFollowerships: UserStoreAction['addProfilesWithFollowerships'] =
    (fieldNames, profiles) => {
      set((state) => {
        const fns = array(fieldNames);
        fns.forEach((fieldName) => {
          const profileField = state.profiles.map(fieldName);
          // const followeeField = state.followerships.map(
          //   FollowershipMapType.Followee,
          // );
          // const followerField = state.followerships.map(
          //   FollowershipMapType.Follower,
          // );
          Object.entries(profiles).forEach(([key, profile]) => {
            profileField.set(key, profile.profile);
            // followeeField.set(key, profile.followeeFollowership);
            // followerField.set(key, profile.followerFollowership);
          });
        });
        return { ...state };
      });
    };

  const getCurrentUser: UserStoreAction['getCurrentUser'] = async (options) => {
    const response = await restGetCurrentUser(options);
    if (response.ok) {
      setupActiveUserInfo(undefined, response.data.user);
    } else {
      console.error('Fail to get current user', response.error.message);
    }
  };

  const getAllowance: UserStoreAction['getAllowance'] = () => {
    return get().allowance;
  };

  const fetchAllowance: UserStoreAction['fetchAllowance'] = async (
    options?,
  ) => {
    const { ok, data } = await restGetUserAllowance(options);

    if (ok) {
      set({ allowance: new Allowance(data.allowance) });
    }
  };

  const verifyEmail: UserStoreAction['verifyEmail'] = async (
    params,
    options,
  ) => {
    const { ok, data } = await verify(params, options);
    if (ok && data.isSuccess) {
      return ResetPasswordResult.Success;
    }
    return ResetPasswordResult.Error;
  };
  const resetPassword: UserStoreAction['resetPassword'] = async (
    params,
    options,
  ) => {
    const { ok, data } = await restResetPassword(params, options);
    if (ok && data.isSuccess) {
      return ResetPasswordResult.Success;
    }
    return ResetPasswordResult.Error;
  };
  const forgotPassword: UserStoreAction['forgotPassword'] = async (
    params,
    options,
  ) => {
    const { ok, data } = await restForgotPassword(params, options);
    if (ok && data.isSuccess) {
      return ResetPasswordResult.Success;
    }
    return ResetPasswordResult.Error;
  };
  const findProfile: UserStoreAction['findProfile'] = async (
    params,
    options,
  ) => {
    const response = await restfindProfile(params, options);
    if (response.ok) {
      set((state) => {
        return { ...state };
      });
    } else {
      console.error('Fail to find profile', response.error.message);
    }
    return response;
  };
  const sendVerification: UserStoreAction['sendVerification'] = async (
    params,
    options,
  ) => {
    // const { ok, data } = await restSendVerification(params, options);
    const response = await restSendVerification(params, options);
    if (response.ok) {
      return ResetPasswordResult.Success;
    }
    return ResetPasswordResult.Error;
  };
  const userCodeValidation: UserStoreAction['userCodeValidation'] = async (
    params,
    options,
  ) => {
    const response = await restUserCodeValidation(params, options);
    if (response.ok) {
      // add profile (if needed)
      return ResetPasswordResult.Success;
    }
    return ResetPasswordResult.Error;
  };

  const getInvitations: UserStoreAction['getInvitations'] = (fieldName) =>
    State.getInvitations(get(), fieldName);

  const getInvitation: UserStoreAction['getInvitation'] = (userId) =>
    State.getInvitation(get(), userId);

  const fetchInvitations: UserStoreAction['fetchInvitations'] = async (
    { invitedBy, type },
    cursor,
    options,
  ) => {
    const { ok, data } = await restGetInvitations(
      {
        invitedBy,
        cursor,
        limit: 20,
        withProfiles: true,
      },
      options,
    );
    if (ok) {
      set((state) => {
        const newCursor = data.cursor ?? null;
        const invitations = data.invitations.map((o) => new Invitation(o));
        if (cursor === undefined) {
          State.setInvitations(state, type, ...invitations);
        } else {
          State.pushInvitations(state, type, ...invitations);
        }
        State.setInvitationCursor(state, type, newCursor);

        return { ...state };
      });

      // add profiles
      useUserStore
        .getState()
        .addProfiles(
          UserListType.Referral,
          ...data.profiles.map((p) => new Profile(p.profile)),
        );
    }
    return {
      shouldFetchMore: !!data?.cursor && data?.invitations.length === 0,
    };
  };

  const fetchInvitation: UserStoreAction['fetchInvitation'] = async (
    options,
  ) => {
    const { ok, data } = await restGetInvitation(
      { withProfiles: true },
      options,
    );
    if (ok) {
      const invitation = data.invitation;
      if (invitation !== null) {
        set((state) => {
          State.addInvitationToMap(
            state,
            InvitationType.Referral,
            invitation.userId,
            new Invitation({ ...invitation, userCode: data.userCode }),
          );
          return { ...state };
        });
      }

      // add profiles
      useUserStore
        .getState()
        .addProfiles(
          UserListType.Referral,
          ...data.profiles.map((p) => new Profile(p.profile)),
        );
    }
  };

  const fetchInvitationUser: UserStoreAction['fetchInvitationUser'] = async (
    userId,
    options,
  ) => {
    const { ok, data } = await restGetInvitationUser(
      { userId },
      { withProfiles: true },
      options,
    );
    if (ok && data !== null) {
      const invitation = data.invitation;
      if (invitation !== null) {
        set((state) => {
          State.addInvitationToMap(
            state,
            InvitationType.SecondReferral,
            invitation.userId,
            new Invitation({ ...invitation, userCode: data.userCode }),
          );
          return { ...state };
        });
      }

      // add profiles
      useUserStore
        .getState()
        .addProfiles(
          UserListType.Referral,
          ...data.profiles.map((p) => new Profile(p.profile)),
        );
    }
  };

  const getSystemInvitations: UserStoreAction['getSystemInvitations'] = (
    fieldName,
  ) => State.getSystemInvitations(get(), fieldName);

  const getSystemInvitation: UserStoreAction['getSystemInvitation'] = (
    systemInvitationId,
  ) => State.getSystemInvitation(get(), systemInvitationId);

  const fetchSystemInvitations: UserStoreAction['fetchSystemInvitations'] =
    async ({ type, reverse, followedUpBy }, cursor, options) => {
      const { ok, data } = await restGetSystemInvitations(
        {
          type,
          reverse,
          followedUpBy,
          cursor,
          limit: 20,
        },
        options,
      );
      if (ok) {
        set((state) => {
          const newCursor = data.cursor ?? null;
          const systemInvitations = data.systemInvitations.map(
            (o) => new SystemInvitation(o),
          );
          if (cursor === undefined) {
            State.setSystemInvitations(state, type, ...systemInvitations);
          } else {
            State.pushSystemInvitations(state, type, ...systemInvitations);
          }
          State.setSystemInvitationCursor(state, type, newCursor);
          return { ...state };
        });
      }
      return {
        shouldFetchMore: !!data?.cursor && data?.systemInvitations.length === 0,
      };
    };

  const fetchSystemInvitation: UserStoreAction['fetchSystemInvitation'] =
    async (systemInvitationId, options) => {
      const systemInvitation = State.getSystemInvitation(
        get(),
        systemInvitationId,
      );
      if (systemInvitation) {
        set((state) => {
          State.addSystemInvitationToMap(
            state,
            systemInvitation.type,
            systemInvitationId,
            systemInvitation,
          );
          return { ...state };
        });
      } else {
        const { ok, data } = await restGetSystemInvitation(
          { systemInvitationId },
          { withProfiles: true },
          options,
        );
        if (ok) {
          set((state) => {
            State.addSystemInvitationToMap(
              state,
              data.systemInvitation.type,
              systemInvitationId,
              new SystemInvitation(data.systemInvitation),
            );
            return { ...state };
          });

          useSpaceStore
            .getState()
            .handleFetchSpacesResult(
              data.systemInvitation.type,
              undefined,
              data.classroom ? [data.classroom] : [],
              data.profiles,
              null,
            );
        }
      }
    };

  const createSystemInvitation: UserStoreAction['createSystemInvitation'] =
    async (body, options) => {
      const { ok, data, error } = await restCreateSystemInvitation(
        body,
        options,
      );
      if (ok) {
        const systemInvitation = new SystemInvitation(data.systemInvitation);
        set((state) => {
          State.pushSystemInvitations(
            state,
            systemInvitation.type,
            systemInvitation,
          );
          return { ...state };
        });
        return systemInvitation.systemInvitationId;
      } else {
        console.error('Fail to create systemInvitation', error.message);
        return undefined;
      }
    };

  const updateSystemInvitation: UserStoreAction['updateSystemInvitation'] =
    async (systemInvitationId, body, options) => {
      const { ok, data, error } = await restUpdateSystemInvitation(
        { systemInvitationId },
        body,
        options,
      );
      if (ok) {
        const systemInvitation = new SystemInvitation(data.systemInvitation);
        set((state) => {
          State.updateSystemInvitation(state, systemInvitation);
          return { ...state };
        });
        return true;
      } else {
        console.error('Fail to update systemInvitation', error.message);
        return false;
      }
    };

  const deleteSystemInvitation: UserStoreAction['deleteSystemInvitation'] =
    async (systemInvitationId, options) => {
      const { ok, error } = await restDeleteSystemInvitation(
        { systemInvitationId },
        options,
      );
      if (ok) {
        set((state) => {
          const systemInvitation = State.getSystemInvitation(
            state,
            systemInvitationId,
          );
          if (systemInvitation)
            State.removeSystemInvitation(state, systemInvitation);
          return { ...state };
        });
        return true;
      } else {
        console.error('Fail to delete systemInvitation', error.message);
        return false;
      }
    };

  const sentInvitationEmail: UserStoreAction['sentInvitationEmail'] = async (
    systemInvitationId,
    options,
  ) => {
    const { ok, data, error } = await restSentInvitationEmail(
      { systemInvitationId },
      options,
    );
    if (ok) {
      set((state) => {
        State.updateSystemInvitation(
          state,
          new SystemInvitation(data.systemInvitation),
        );
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to sent InvitationEmail', error.message);
      return false;
    }
  };

  const acceptInvitationEmail: UserStoreAction['acceptInvitationEmail'] =
    async (systemInvitationId, body, options) => {
      const { ok, data, error } = await restAcceptInvitationEmail(
        { systemInvitationId },
        body,
        options,
      );
      if (ok) {
        set((state) => {
          State.updateSystemInvitation(
            state,
            new SystemInvitation(data.systemInvitation),
          );
          return { ...state };
        });
        return true;
      } else {
        console.error('Fail to claim InvitationEmail', error.message);
        return false;
      }
    };

  const sentPasswordEmail: UserStoreAction['sentPasswordEmail'] = async (
    systemInvitationId,
    options,
  ) => {
    const { ok, data, error } = await restSentPasswordEmail(
      { systemInvitationId },
      options,
    );
    if (ok) {
      set((state) => {
        State.updateSystemInvitation(
          state,
          new SystemInvitation(data.systemInvitation),
        );
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to claim InvitationEmail', error.message);
      return false;
    }
  };

  const getInvitationLink: UserStoreAction['getInvitationLink'] = (
    invitationLinkId,
  ) => get().invitationLinks.get(invitationLinkId);

  const fetchInvitationLink: UserStoreAction['fetchInvitationLink'] = async (
    invitationLinkId,
  ) => {
    // const invitationLink = getInvitationLink(invitationLinkId);
    // Fetch from server
    const { ok, data } = await restGetInvitationLink(
      { invitationLinkId },
      { withProfiles: true },
    );
    if (ok) {
      const space = data.data.classroom;
      const album = data.data.gameSet;
      const spaceTeam = data.data.team;
      const leaderboard = data.data.leaderboard;
      const game = data.data.game;
      const multiplayerGame = data.data.multiplayerGame;
      const imageUrls = data.data.imageUrls;

      set((state) => {
        State.setInvitationLinks(
          state,
          UserListType.ShareContent,
          new InvitationLink(data.invitationLink),
        );
        return { ...state };
      });

      // add profiles
      useUserStore
        .getState()
        .addProfiles(
          UserListType.ShareContent,
          ...data.profiles.map((p) => new Profile(p.profile)),
        );

      if (space) {
        useSpaceStore.getState().addSpaceToField(SpaceListType.Recent, space);
      }

      if (album) {
        useSpaceStore.getState().addAlbumToField(AlbumListType.Recent, album);
      }
      if (spaceTeam) {
        useSpaceStore
          .getState()
          .addTeamToField(SpaceTeamListType.Recent, spaceTeam);
      }

      if (leaderboard) {
        useSpaceStore
          .getState()
          .addLeaderboardToField(SpaceLeaderboardListType.Recent, leaderboard);
      }
      if (game) {
        useSpaceStore.getState().addGameToField(GameListType.Recent, game);
      }
      if (multiplayerGame) {
        useSpaceStore
          .getState()
          .addMultiplayerGameToField(
            MultiplayerGameListType.Recent,
            multiplayerGame,
          );
      }

      if (imageUrls) {
        Object.entries(imageUrls).forEach(([fileId, url]) => {
          useFileStore.getState().setFileUrl(fileId, url);
        });
      }

      // classroomId -> profile <owner>
      if (data.data.rootId && space) {
        const rootProfile = useUserStore
          .getState()
          .getProfile(data.data.rootId);

        if (rootProfile) {
          useUserStore
            .getState()
            .profiles.map(ProfileMapType.SpaceRoot)
            .set(space.classroomId, rootProfile);
        }
      }
    }
  };

  const getRedemptionCode: UserStoreAction['getRedemptionCode'] = (
    redemptionCode,
  ) => get().redemptionCodes.get(redemptionCode);
  const fetchRedemptionCode: UserStoreAction['fetchRedemptionCode'] = async (
    redemptionCode,
  ) => {
    // const invitationLink = getInvitationLink(invitationLinkId);
    // Fetch from server
    const { ok, data } = await restGetRedemptionCode({ redemptionCode });
    if (ok) {
      set((state) => {
        State.setRedemptionCodes(
          state,
          RedemptionCodeListType.Recent,
          new RedemptionCode(data.redemptionCode),
        );
        return { ...state };
      });
    }
  };

  const getNameCard: UserStoreAction['getNameCard'] = (nameCardId) =>
    get().nameCards.get(nameCardId);

  const fetchNameCard: UserStoreAction['fetchNameCard'] = async (
    nameCardId,
  ) => {
    const nameCard = getNameCard(nameCardId);
    if (nameCard) {
      set((state) => {
        State.setNameCards(state, NameCardListType.Collect, nameCard);
        return { ...state };
      });
      return;
    } else {
      // Fetch from server
      const { ok, data } = await restGetNameCard({ nameCardId });
      if (ok) {
        const card = data.nameCard;
        const space = data.classroom;
        if (card) {
          set((state) => {
            State.setNameCards(
              state,
              NameCardListType.Collect,
              new NameCard(card),
            );
            return { ...state };
          });
        }
        if (space) {
          useSpaceStore
            .getState()
            .addSpaceToField(SystemInvitationType.OwnerShipTransfer, space);
        }
      }
    }
  };

  const collectNameCard: UserStoreAction['collectNameCard'] = async (
    nameCardId,
    body,
    options,
  ): Promise<RestResponse<CollectedNameCardData>> => {
    const response = await restCollectNameCard({ nameCardId }, body, options);
    if (response.ok) {
      set((state) => {
        State.updateNameCard(state, new NameCard(response.data.nameCard));
        return { ...state };
      });
    } else {
      console.error('Fail to collect nameCard', response.error.message);
    }
    return response;
  };

  const acceptNameCardTransfer: UserStoreAction['acceptNameCardTransfer'] =
    async (nameCardId, body, options) => {
      const response = await restAcceptNameCardTransfer(
        { nameCardId },
        body,
        options,
      );
      if (response.ok) {
        set((state) => {
          State.updateNameCard(state, new NameCard(response.data.nameCard));
          return { ...state };
        });
      } else {
        console.error('Fail to accept transfer', response.error.message);
      }
      return response;
    };

  return {
    initialize,
    shutdown,
    profiles: new ProfileRepository(),
    accounts: new AccountRepository(),
    invitations: new InvitationRepository(),
    systemInvitations: new SystemInvitationRepository(),
    invitationLinks: new InvitationLinkRepository(),
    redemptionCodes: new RedemptionCodeRepository(),
    nameCards: new NameCardRepository(),
    allowance: undefined,
    setupActiveUserInfo,
    setProfiles,
    addProfiles,
    setAccounts,
    addAccounts,
    getProfiles,
    getProfile,
    getAccount,
    searchProfiles,
    getProfileFromMap,
    deleteProfileFromMap,
    addProfilesWithFollowerships,
    getCurrentUser,
    getAllowance,
    fetchAllowance,
    verifyEmail,
    resetPassword,
    forgotPassword,
    findProfile,
    sendVerification,
    userCodeValidation,
    getInvitation,
    getInvitations,
    fetchInvitations,
    fetchInvitation,
    fetchInvitationUser,
    getSystemInvitation,
    getSystemInvitations,
    fetchSystemInvitation,
    fetchSystemInvitations,
    createSystemInvitation,
    updateSystemInvitation,
    deleteSystemInvitation,
    sentInvitationEmail,
    acceptInvitationEmail,
    sentPasswordEmail,
    getInvitationLink,
    fetchInvitationLink,
    getNameCard,
    fetchNameCard,
    collectNameCard,
    acceptNameCardTransfer,
    fetchRedemptionCode,
    getRedemptionCode,
  };
};

const persistOptions: PersistOptions<Store, Partial<UserStoreState>> = {
  name: 'user-storage',
  // getStorage: () => AsyncStorage,
  deserialize: (str: string): StorageValue<Partial<UserStoreState>> => {
    try {
      const { state, version } = JSON.parse(
        str,
        dateNumberReviver,
      ) as StorageValue<UserStoreStateJson>;
      return {
        state: {
          profiles: new ProfileRepository(state.profiles),
          accounts: new AccountRepository(state.accounts),
          invitations: new InvitationRepository(state.invitations),
          systemInvitations: new SystemInvitationRepository(
            state.systemInvitations,
          ),
          invitationLinks: new InvitationLinkRepository(state.invitationLinks),
          redemptionCodes: new RedemptionCodeRepository(state.redemptionCodes),
          allowance: state.allowance
            ? new Allowance(state.allowance)
            : undefined,
        },
        version,
      };
    } catch (err) {
      console.error('Fail to deserialize user-storage', str);
      console.error(err);
      return { state: {} };
    }
  },
  onRehydrateStorage: () => {
    return () => {
      lifecycle.finishHydration();
      // TODO: handle rehydrate error case by shutdown?
    };
  },
};

export const useUserStore = createWithEqualityFn<Store>()(
  persist(initializer, persistOptions),
  Object.is,
);

export const useUserStoreListField = createStoreListFieldHook(useUserStore);
export const useUserStoreMapField = createStoreMapFieldHook(useUserStore);
